import { Skeleton } from '@/components/ui/skeleton'
import { useDashboardSummaries } from '@/hooks/queries/dashboards/useDashboardList'
import { cn } from '@/lib/utils'
import { useParams } from '@tanstack/react-router'
import { SkeletonRegion, fitCount, useElementSize, useStableKeys } from './_shared'

export interface StaticTextSkeletonProps {
	className?: string
	label?: string | false
}

const PARAGRAPH_LINE_HEIGHT = 26
const PARAGRAPH_WIDTHS = ['w-[75%]', 'w-[55%]', 'w-[85%]', 'w-[35%]', 'w-[60%]', 'w-[70%]', 'w-[45%]', 'w-[80%]']
// Heading block + padding, excluded from the space the paragraph lines can use.
const HEADING_BLOCK_HEIGHT = 84

export function StaticTextSkeleton({ className, label = 'Loading' }: StaticTextSkeletonProps) {
	// Attempt to get the active dashboard name from URL params and the store
	const params = useParams({ strict: false }) as { dashboardId?: string }
	const dashboardId = params?.dashboardId
	const { dashboards } = useDashboardSummaries()
	const dashboardName = dashboards.find(d => String(d.id) === String(dashboardId))?.title

	// If dashboardName exists, split it by spaces to mimic the word layout.
	// Otherwise, fallback to a standard loading placeholder structure.
	const words = dashboardName ? dashboardName.split(/\s+/) : ['Loading', 'Dashboard']

	const [rootRef, size] = useElementSize<HTMLDivElement>()
	const lineKeys = useStableKeys(fitCount(size.height - HEADING_BLOCK_HEIGHT, PARAGRAPH_LINE_HEIGHT, 1, 12))

	return (
		<SkeletonRegion
			ref={rootRef}
			label={label}
			className={cn('flex size-full items-start justify-start overflow-hidden p-6', className)}>
			<div className='flex flex-col items-start gap-4 w-full'>
				{/* 1. Heading (left-aligned, short dashboard title) */}
				<div className='flex flex-wrap items-center gap-x-2 gap-y-1.5 shrink-0'>
					{words.map((word, i) => (
						<Skeleton
							// biome-ignore lint/suspicious/noArrayIndexKey: key is stable for skeleton rendering
							key={`${word}-${i}`}
							className='h-5 rounded bg-accent/50 animate-pulse'
							style={{ width: `${Math.max(3, word.length * 1.15)}ch` }}
						/>
					))}
				</div>

				{/* 2. Left-aligned body paragraph skeletons mirroring the user's reference image */}
				<div className='flex flex-col items-start gap-2.5 w-full mt-1.5'>
					{lineKeys.map((key, i) => (
						<Skeleton
							key={key}
							className={cn('h-4 rounded bg-accent/30 animate-pulse', PARAGRAPH_WIDTHS[i % PARAGRAPH_WIDTHS.length])}
						/>
					))}
				</div>
			</div>
		</SkeletonRegion>
	)
}
